import * as FileSaver from 'file-saver';
import * as Bixby from '$/core';

export abstract class UserAdminService {
  public static adminBaseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_USERCONTROLLER}`;

  public static async getAll(): Promise<Bixby.Models.UserSlim[]> {
    const route = Bixby.API.ServerRoute.forAction(UserAdminService.adminBaseUrl);
    const results: Bixby.Models.UserSlim[] = await Bixby.API.get(route);

    if (results) {
      results.forEach(result => {
        result.firstLoginDate = Bixby.Utils.parseUtcDateFromApi(result.firstLoginDate as any);
        result.lastLoginDate = Bixby.Utils.parseUtcDateFromApi(result.lastLoginDate as any);
      });
    }

    return results;
  }

  public static async create(newUser: Bixby.Models.CreateUserRequest): Promise<Bixby.Models.User> {
    const route = Bixby.API.ServerRoute.forAction(UserAdminService.adminBaseUrl);
    return await Bixby.API.post(route, newUser);
  }

  public static async getById(id: string): Promise<Bixby.Models.User> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${id}`);
    return await Bixby.API.get(route);
  }

  public static async putById(updateRequest: Bixby.Models.UpdateUserRequest): Promise<Bixby.Models.User> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${updateRequest.id}`);
    return await Bixby.API.put(route, updateRequest);
  }

  public static async deleteById(user: Bixby.Models.UserSlim): Promise<boolean> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${user.id}`);
    return await Bixby.API.delete(route);
  }

  public static async importUsers(clientId: string, usersFile: File): Promise<Bixby.Models.User> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${clientId}/${Bixby.Constants.API_ACTION_IMPORT}`);
    const formData = new FormData();
    formData.append('usersFile', usersFile);
    return await Bixby.API.post(route, formData);
  }

  public static async getTemplateFile(): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${Bixby.Constants.API_ACTION_IMPORT_TEMPLATE}`);
    const blob = await Bixby.API.getFile(route);
    FileSaver.saveAs(blob, "ImportTemplate.xlsx");
    }

  public static async resendEmailById(userId: string): Promise<boolean> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${userId}/resendemail`);
    return await Bixby.API.post(route, userId);
  }

  private static readonly apiToUserStatus: Record<string, Bixby.Enums.UserStatus> = {
    Available: Bixby.Enums.UserStatus.Available,
    Break: Bixby.Enums.UserStatus.Break,
    OutOfOffice: Bixby.Enums.UserStatus.OutOfOffice,
    Busy: Bixby.Enums.UserStatus.Busy,
    NoTasks: Bixby.Enums.UserStatus.NoTasks,
  };

  private static readonly userStatusToApi: Record<Bixby.Enums.UserStatus, string> = {
    [Bixby.Enums.UserStatus.Available]: 'Available',
    [Bixby.Enums.UserStatus.Break]: 'Break',
    [Bixby.Enums.UserStatus.OutOfOffice]: 'OutOfOffice',
    [Bixby.Enums.UserStatus.Busy]: 'Busy',
    [Bixby.Enums.UserStatus.NoTasks]: 'NoTasks',
  };

  public static fromApiStatus(raw: string): Bixby.Enums.UserStatus | undefined {
    return UserAdminService.apiToUserStatus[raw];
  }

  public static async updateStatusById(id: string, status: Bixby.Enums.UserStatus): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/${id}/status`);
    const request = { userId: id, status: UserAdminService.userStatusToApi[status] };
    await Bixby.API.put(route, request);
  }

  public static async getAssignees(): Promise<Bixby.Models.AssignableUser[]> {
    const route = Bixby.API.ServerRoute.forAction(`${UserAdminService.adminBaseUrl}/assignees`);
    const results: { id: string; name: string; status: string }[] = await Bixby.API.get(route);
    return (results ?? []).map(r => ({
      id: r.id,
      name: r.name,
      status: UserAdminService.apiToUserStatus[r.status] ?? Bixby.Enums.UserStatus.Available,
    }));
  }
}
